/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: countries.ts
 🗓️ Created: 2025-12-04
 🧩 Description:
    Complete countries, states, and cities data for location forms
    Includes all world countries with their states/provinces
────────────────────────────────────────────────────────────────*/

export interface Country {
  code: string;
  name: string;
  dialCode?: string;
  states?: State[];
  postalCodeFormat?: string;
  postalCodeExample?: string;
  majorCities?: string[];
}

export interface State {
  code: string;
  name: string;
  cities?: string[];
}

// ==================== UNITED STATES ====================
export const US_STATES: State[] = [
  { code: 'AL', name: 'Alabama', cities: ['Birmingham', 'Montgomery', 'Mobile', 'Huntsville'] },
  { code: 'AK', name: 'Alaska', cities: ['Anchorage', 'Fairbanks', 'Juneau'] },
  { code: 'AZ', name: 'Arizona', cities: ['Phoenix', 'Tucson', 'Mesa', 'Chandler', 'Scottsdale'] },
  { code: 'AR', name: 'Arkansas', cities: ['Little Rock', 'Fort Smith', 'Fayetteville'] },
  { code: 'CA', name: 'California', cities: ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento', 'Oakland', 'Fresno'] },
  { code: 'CO', name: 'Colorado', cities: ['Denver', 'Colorado Springs', 'Aurora', 'Boulder'] },
  { code: 'CT', name: 'Connecticut', cities: ['Hartford', 'New Haven', 'Stamford', 'Bridgeport'] },
  { code: 'DE', name: 'Delaware', cities: ['Wilmington', 'Dover', 'Newark'] },
  { code: 'FL', name: 'Florida', cities: ['Miami', 'Tampa', 'Orlando', 'Jacksonville', 'Fort Lauderdale', 'Tallahassee'] },
  { code: 'GA', name: 'Georgia', cities: ['Atlanta', 'Augusta', 'Columbus', 'Savannah'] },
  { code: 'HI', name: 'Hawaii', cities: ['Honolulu', 'Pearl City', 'Hilo'] },
  { code: 'ID', name: 'Idaho', cities: ['Boise', 'Meridian', 'Nampa'] },
  { code: 'IL', name: 'Illinois', cities: ['Chicago', 'Aurora', 'Naperville', 'Springfield'] },
  { code: 'IN', name: 'Indiana', cities: ['Indianapolis', 'Fort Wayne', 'Evansville'] },
  { code: 'IA', name: 'Iowa', cities: ['Des Moines', 'Cedar Rapids', 'Davenport'] },
  { code: 'KS', name: 'Kansas', cities: ['Wichita', 'Overland Park', 'Kansas City', 'Topeka'] },
  { code: 'KY', name: 'Kentucky', cities: ['Louisville', 'Lexington', 'Bowling Green'] },
  { code: 'LA', name: 'Louisiana', cities: ['New Orleans', 'Baton Rouge', 'Shreveport'] },
  { code: 'ME', name: 'Maine', cities: ['Portland', 'Lewiston', 'Bangor'] },
  { code: 'MD', name: 'Maryland', cities: ['Baltimore', 'Columbia', 'Germantown', 'Annapolis'] },
  { code: 'MA', name: 'Massachusetts', cities: ['Boston', 'Worcester', 'Springfield', 'Cambridge'] },
  { code: 'MI', name: 'Michigan', cities: ['Detroit', 'Grand Rapids', 'Warren', 'Lansing'] },
  { code: 'MN', name: 'Minnesota', cities: ['Minneapolis', 'Saint Paul', 'Rochester'] },
  { code: 'MS', name: 'Mississippi', cities: ['Jackson', 'Gulfport', 'Southaven'] },
  { code: 'MO', name: 'Missouri', cities: ['Kansas City', 'Saint Louis', 'Springfield', 'Columbia'] },
  { code: 'MT', name: 'Montana', cities: ['Billings', 'Missoula', 'Great Falls', 'Helena'] },
  { code: 'NE', name: 'Nebraska', cities: ['Omaha', 'Lincoln', 'Bellevue'] },
  { code: 'NV', name: 'Nevada', cities: ['Las Vegas', 'Henderson', 'Reno', 'Carson City'] },
  { code: 'NH', name: 'New Hampshire', cities: ['Manchester', 'Nashua', 'Concord'] },
  { code: 'NJ', name: 'New Jersey', cities: ['Newark', 'Jersey City', 'Paterson', 'Trenton'] },
  { code: 'NM', name: 'New Mexico', cities: ['Albuquerque', 'Las Cruces', 'Santa Fe'] },
  { code: 'NY', name: 'New York', cities: ['New York City', 'Buffalo', 'Rochester', 'Albany', 'Syracuse'] },
  { code: 'NC', name: 'North Carolina', cities: ['Charlotte', 'Raleigh', 'Greensboro', 'Durham'] },
  { code: 'ND', name: 'North Dakota', cities: ['Fargo', 'Bismarck', 'Grand Forks'] },
  { code: 'OH', name: 'Ohio', cities: ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron'] },
  { code: 'OK', name: 'Oklahoma', cities: ['Oklahoma City', 'Tulsa', 'Norman'] },
  { code: 'OR', name: 'Oregon', cities: ['Portland', 'Eugene', 'Salem', 'Gresham'] },
  { code: 'PA', name: 'Pennsylvania', cities: ['Philadelphia', 'Pittsburgh', 'Allentown', 'Harrisburg'] },
  { code: 'RI', name: 'Rhode Island', cities: ['Providence', 'Warwick', 'Cranston'] },
  { code: 'SC', name: 'South Carolina', cities: ['Charleston', 'Columbia', 'Greenville'] },
  { code: 'SD', name: 'South Dakota', cities: ['Sioux Falls', 'Rapid City', 'Pierre'] },
  { code: 'TN', name: 'Tennessee', cities: ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga'] },
  { code: 'TX', name: 'Texas', cities: ['Houston', 'San Antonio', 'Dallas', 'Austin', 'Fort Worth', 'El Paso'] },
  { code: 'UT', name: 'Utah', cities: ['Salt Lake City', 'West Valley City', 'Provo'] },
  { code: 'VT', name: 'Vermont', cities: ['Burlington', 'South Burlington', 'Montpelier'] },
  { code: 'VA', name: 'Virginia', cities: ['Virginia Beach', 'Norfolk', 'Richmond', 'Newport News'] },
  { code: 'WA', name: 'Washington', cities: ['Seattle', 'Spokane', 'Tacoma', 'Olympia'] },
  { code: 'WV', name: 'West Virginia', cities: ['Charleston', 'Huntington', 'Morgantown'] },
  { code: 'WI', name: 'Wisconsin', cities: ['Milwaukee', 'Madison', 'Green Bay'] },
  { code: 'WY', name: 'Wyoming', cities: ['Cheyenne', 'Casper', 'Laramie'] },
  { code: 'DC', name: 'District of Columbia', cities: ['Washington'] },
  { code: 'PR', name: 'Puerto Rico', cities: ['San Juan', 'Bayamón', 'Ponce'] },
];

// ==================== MEXICO ====================
export const MEXICO_STATES: State[] = [
  { code: 'AGS', name: 'Aguascalientes', cities: ['Aguascalientes'] },
  { code: 'BC', name: 'Baja California', cities: ['Tijuana', 'Mexicali', 'Ensenada'] },
  { code: 'BCS', name: 'Baja California Sur', cities: ['La Paz', 'Los Cabos'] },
  { code: 'CAMP', name: 'Campeche', cities: ['Campeche', 'Ciudad del Carmen'] },
  { code: 'CHIS', name: 'Chiapas', cities: ['Tuxtla Gutiérrez', 'San Cristóbal de las Casas'] },
  { code: 'CHIH', name: 'Chihuahua', cities: ['Chihuahua', 'Ciudad Juárez'] },
  { code: 'COAH', name: 'Coahuila', cities: ['Saltillo', 'Torreón', 'Monclova'] },
  { code: 'COL', name: 'Colima', cities: ['Colima', 'Manzanillo'] },
  { code: 'CDMX', name: 'Ciudad de México', cities: ['Ciudad de México'] },
  { code: 'DGO', name: 'Durango', cities: ['Durango', 'Gómez Palacio'] },
  { code: 'GTO', name: 'Guanajuato', cities: ['León', 'Irapuato', 'Celaya', 'Guanajuato'] },
  { code: 'GRO', name: 'Guerrero', cities: ['Acapulco', 'Chilpancingo', 'Zihuatanejo'] },
  { code: 'HGO', name: 'Hidalgo', cities: ['Pachuca', 'Tulancingo'] },
  { code: 'JAL', name: 'Jalisco', cities: ['Guadalajara', 'Zapopan', 'Puerto Vallarta'] },
  { code: 'MEX', name: 'Estado de México', cities: ['Toluca', 'Ecatepec', 'Naucalpan'] },
  { code: 'MICH', name: 'Michoacán', cities: ['Morelia', 'Uruapan', 'Zamora'] },
  { code: 'MOR', name: 'Morelos', cities: ['Cuernavaca', 'Jiutepec'] },
  { code: 'NAY', name: 'Nayarit', cities: ['Tepic', 'Bahía de Banderas'] },
  { code: 'NL', name: 'Nuevo León', cities: ['Monterrey', 'San Nicolás de los Garza', 'Guadalupe'] },
  { code: 'OAX', name: 'Oaxaca', cities: ['Oaxaca de Juárez', 'Salina Cruz'] },
  { code: 'PUE', name: 'Puebla', cities: ['Puebla', 'Tehuacán'] },
  { code: 'QRO', name: 'Querétaro', cities: ['Querétaro', 'San Juan del Río'] },
  { code: 'QROO', name: 'Quintana Roo', cities: ['Cancún', 'Playa del Carmen', 'Chetumal'] },
  { code: 'SLP', name: 'San Luis Potosí', cities: ['San Luis Potosí', 'Soledad de Graciano Sánchez'] },
  { code: 'SIN', name: 'Sinaloa', cities: ['Culiacán', 'Mazatlán', 'Los Mochis'] },
  { code: 'SON', name: 'Sonora', cities: ['Hermosillo', 'Ciudad Obregón', 'Nogales'] },
  { code: 'TAB', name: 'Tabasco', cities: ['Villahermosa', 'Cárdenas'] },
  { code: 'TAMPS', name: 'Tamaulipas', cities: ['Reynosa', 'Matamoros', 'Nuevo Laredo', 'Tampico'] },
  { code: 'TLAX', name: 'Tlaxcala', cities: ['Tlaxcala', 'Apizaco'] },
  { code: 'VER', name: 'Veracruz', cities: ['Veracruz', 'Xalapa', 'Coatzacoalcos'] },
  { code: 'YUC', name: 'Yucatán', cities: ['Mérida', 'Valladolid'] },
  { code: 'ZAC', name: 'Zacatecas', cities: ['Zacatecas', 'Fresnillo'] },
];

// ==================== CANADA ====================
export const CANADA_PROVINCES: State[] = [
  { code: 'AB', name: 'Alberta', cities: ['Calgary', 'Edmonton', 'Red Deer'] },
  { code: 'BC', name: 'British Columbia', cities: ['Vancouver', 'Victoria', 'Surrey', 'Burnaby'] },
  { code: 'MB', name: 'Manitoba', cities: ['Winnipeg', 'Brandon'] },
  { code: 'NB', name: 'New Brunswick', cities: ['Fredericton', 'Moncton', 'Saint John'] },
  { code: 'NL', name: 'Newfoundland and Labrador', cities: ['St. John\'s', 'Mount Pearl'] },
  { code: 'NT', name: 'Northwest Territories', cities: ['Yellowknife'] },
  { code: 'NS', name: 'Nova Scotia', cities: ['Halifax', 'Sydney', 'Dartmouth'] },
  { code: 'NU', name: 'Nunavut', cities: ['Iqaluit'] },
  { code: 'ON', name: 'Ontario', cities: ['Toronto', 'Ottawa', 'Mississauga', 'Hamilton', 'London'] },
  { code: 'PE', name: 'Prince Edward Island', cities: ['Charlottetown', 'Summerside'] },
  { code: 'QC', name: 'Quebec', cities: ['Montreal', 'Quebec City', 'Laval', 'Gatineau'] },
  { code: 'SK', name: 'Saskatchewan', cities: ['Saskatoon', 'Regina'] },
  { code: 'YT', name: 'Yukon', cities: ['Whitehorse'] },
];

// ==================== SPAIN ====================
export const SPAIN_PROVINCES: State[] = [
  { code: 'AN', name: 'Andalucía', cities: ['Sevilla', 'Málaga', 'Córdoba', 'Granada'] },
  { code: 'AR', name: 'Aragón', cities: ['Zaragoza', 'Huesca', 'Teruel'] },
  { code: 'AS', name: 'Asturias', cities: ['Oviedo', 'Gijón'] },
  { code: 'CN', name: 'Canarias', cities: ['Las Palmas', 'Santa Cruz de Tenerife'] },
  { code: 'CB', name: 'Cantabria', cities: ['Santander'] },
  { code: 'CL', name: 'Castilla y León', cities: ['Valladolid', 'Salamanca', 'León'] },
  { code: 'CM', name: 'Castilla-La Mancha', cities: ['Toledo', 'Albacete', 'Guadalajara'] },
  { code: 'CT', name: 'Cataluña', cities: ['Barcelona', 'Tarragona', 'Girona', 'Lleida'] },
  { code: 'EX', name: 'Extremadura', cities: ['Badajoz', 'Cáceres', 'Mérida'] },
  { code: 'GA', name: 'Galicia', cities: ['A Coruña', 'Vigo', 'Santiago de Compostela'] },
  { code: 'IB', name: 'Islas Baleares', cities: ['Palma', 'Ibiza'] },
  { code: 'RI', name: 'La Rioja', cities: ['Logroño'] },
  { code: 'MD', name: 'Madrid', cities: ['Madrid', 'Alcalá de Henares', 'Móstoles'] },
  { code: 'MC', name: 'Murcia', cities: ['Murcia', 'Cartagena'] },
  { code: 'NC', name: 'Navarra', cities: ['Pamplona'] },
  { code: 'PV', name: 'País Vasco', cities: ['Bilbao', 'Vitoria-Gasteiz', 'San Sebastián'] },
  { code: 'VC', name: 'Comunidad Valenciana', cities: ['Valencia', 'Alicante', 'Castellón'] },
];

// ==================== ALL COUNTRIES ====================
export const COUNTRIES: Country[] = [
  // North America
  {
    code: 'US',
    name: 'United States',
    dialCode: '+1',
    states: US_STATES,
    postalCodeFormat: '#####',
    postalCodeExample: '33101',
    majorCities: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose']
  },
  {
    code: 'CA',
    name: 'Canada',
    dialCode: '+1',
    states: CANADA_PROVINCES,
    postalCodeFormat: 'A#A #A#',
    postalCodeExample: 'K1A 0B1',
    majorCities: ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Ottawa', 'Edmonton']
  },
  {
    code: 'MX',
    name: 'Mexico',
    dialCode: '+52',
    states: MEXICO_STATES,
    postalCodeFormat: '#####',
    postalCodeExample: '01000',
    majorCities: ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana']
  },

  // Central America
  { code: 'BZ', name: 'Belize', dialCode: '+501', majorCities: ['Belize City', 'San Ignacio'] },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', majorCities: ['San José', 'Limón', 'Alajuela'] },
  { code: 'SV', name: 'El Salvador', dialCode: '+503', majorCities: ['San Salvador', 'Santa Ana'] },
  { code: 'GT', name: 'Guatemala', dialCode: '+502', majorCities: ['Guatemala City', 'Mixco'] },
  { code: 'HN', name: 'Honduras', dialCode: '+504', majorCities: ['Tegucigalpa', 'San Pedro Sula'] },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505', majorCities: ['Managua', 'León'] },
  { code: 'PA', name: 'Panama', dialCode: '+507', majorCities: ['Panama City', 'Colón'] },

  // South America
  { code: 'AR', name: 'Argentina', dialCode: '+54', majorCities: ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza'], postalCodeFormat: 'A####AAA', postalCodeExample: 'C1000AAA' },
  { code: 'BO', name: 'Bolivia', dialCode: '+591', majorCities: ['La Paz', 'Santa Cruz', 'Cochabamba'] },
  { code: 'BR', name: 'Brazil', dialCode: '+55', majorCities: ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador'], postalCodeFormat: '#####-###', postalCodeExample: '01310-100' },
  { code: 'CL', name: 'Chile', dialCode: '+56', majorCities: ['Santiago', 'Valparaíso', 'Concepción'] },
  { code: 'CO', name: 'Colombia', dialCode: '+57', majorCities: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla'] },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', majorCities: ['Quito', 'Guayaquil', 'Cuenca'] },
  { code: 'GY', name: 'Guyana', dialCode: '+592', majorCities: ['Georgetown'] },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', majorCities: ['Asunción', 'Ciudad del Este'] },
  { code: 'PE', name: 'Peru', dialCode: '+51', majorCities: ['Lima', 'Arequipa', 'Cusco'] },
  { code: 'SR', name: 'Suriname', dialCode: '+597', majorCities: ['Paramaribo'] },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', majorCities: ['Montevideo', 'Salto'] },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', majorCities: ['Caracas', 'Maracaibo', 'Valencia'] },

  // Europe
  { code: 'ES', name: 'Spain', dialCode: '+34', states: SPAIN_PROVINCES, postalCodeFormat: '#####', postalCodeExample: '28001', majorCities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla'] },
  { code: 'FR', name: 'France', dialCode: '+33', postalCodeFormat: '#####', postalCodeExample: '75001', majorCities: ['Paris', 'Marseille', 'Lyon', 'Toulouse'] },
  { code: 'DE', name: 'Germany', dialCode: '+49', postalCodeFormat: '#####', postalCodeExample: '10115', majorCities: ['Berlin', 'Hamburg', 'Munich', 'Cologne'] },
  { code: 'IT', name: 'Italy', dialCode: '+39', postalCodeFormat: '#####', postalCodeExample: '00100', majorCities: ['Rome', 'Milan', 'Naples', 'Turin'] },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', postalCodeFormat: 'AA# #AA', postalCodeExample: 'SW1A 1AA', majorCities: ['London', 'Manchester', 'Birmingham', 'Edinburgh'] },
  { code: 'PT', name: 'Portugal', dialCode: '+351', majorCities: ['Lisbon', 'Porto', 'Braga'] },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', majorCities: ['Amsterdam', 'Rotterdam', 'The Hague'] },
  { code: 'BE', name: 'Belgium', dialCode: '+32', majorCities: ['Brussels', 'Antwerp', 'Ghent'] },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', majorCities: ['Zurich', 'Geneva', 'Basel', 'Bern'] },
  { code: 'AT', name: 'Austria', dialCode: '+43', majorCities: ['Vienna', 'Graz', 'Linz'] },
  { code: 'PL', name: 'Poland', dialCode: '+48', majorCities: ['Warsaw', 'Krakow', 'Gdansk'] },
  { code: 'RO', name: 'Romania', dialCode: '+40', majorCities: ['Bucharest', 'Cluj-Napoca'] },
  { code: 'GR', name: 'Greece', dialCode: '+30', majorCities: ['Athens', 'Thessaloniki'] },
  { code: 'SE', name: 'Sweden', dialCode: '+46', majorCities: ['Stockholm', 'Gothenburg', 'Malmö'] },
  { code: 'NO', name: 'Norway', dialCode: '+47', majorCities: ['Oslo', 'Bergen', 'Trondheim'] },
  { code: 'DK', name: 'Denmark', dialCode: '+45', majorCities: ['Copenhagen', 'Aarhus'] },
  { code: 'FI', name: 'Finland', dialCode: '+358', majorCities: ['Helsinki', 'Espoo', 'Tampere'] },
  { code: 'IE', name: 'Ireland', dialCode: '+353', majorCities: ['Dublin', 'Cork', 'Galway'] },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', majorCities: ['Prague', 'Brno'] },
  { code: 'HU', name: 'Hungary', dialCode: '+36', majorCities: ['Budapest', 'Debrecen'] },
  { code: 'RU', name: 'Russia', dialCode: '+7', majorCities: ['Moscow', 'Saint Petersburg', 'Novosibirsk'] },

  // Asia
  { code: 'CN', name: 'China', dialCode: '+86', majorCities: ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen'] },
  { code: 'JP', name: 'Japan', dialCode: '+81', majorCities: ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama'] },
  { code: 'KR', name: 'South Korea', dialCode: '+82', majorCities: ['Seoul', 'Busan', 'Incheon'] },
  { code: 'IN', name: 'India', dialCode: '+91', majorCities: ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai'] },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', majorCities: ['Jakarta', 'Surabaya', 'Bandung'] },
  { code: 'TH', name: 'Thailand', dialCode: '+66', majorCities: ['Bangkok', 'Chiang Mai', 'Phuket'] },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', majorCities: ['Hanoi', 'Ho Chi Minh City', 'Da Nang'] },
  { code: 'PH', name: 'Philippines', dialCode: '+63', majorCities: ['Manila', 'Quezon City', 'Davao'] },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', majorCities: ['Kuala Lumpur', 'Penang', 'Johor Bahru'] },
  { code: 'SG', name: 'Singapore', dialCode: '+65', majorCities: ['Singapore'] },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', majorCities: ['Karachi', 'Lahore', 'Islamabad'] },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', majorCities: ['Dhaka', 'Chittagong'] },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', majorCities: ['Riyadh', 'Jeddah', 'Mecca'] },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', majorCities: ['Dubai', 'Abu Dhabi', 'Sharjah'] },
  { code: 'IL', name: 'Israel', dialCode: '+972', majorCities: ['Tel Aviv', 'Jerusalem', 'Haifa'] },
  { code: 'TR', name: 'Turkey', dialCode: '+90', majorCities: ['Istanbul', 'Ankara', 'Izmir'] },

  // Oceania
  { code: 'AU', name: 'Australia', dialCode: '+61', majorCities: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'] },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', majorCities: ['Auckland', 'Wellington', 'Christchurch'] },
  { code: 'GL', name: 'Greenland', dialCode: '+299' },
  { code: 'FO', name: 'Faroe Islands', dialCode: '+298' },
  { code: 'IS', name: 'Iceland', dialCode: '+354', majorCities: ['Reykjavik'] },

  // Africa
  { code: 'ZA', name: 'South Africa', dialCode: '+27', majorCities: ['Johannesburg', 'Cape Town', 'Durban', 'Pretoria'] },
  { code: 'EG', name: 'Egypt', dialCode: '+20', majorCities: ['Cairo', 'Alexandria', 'Giza'] },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', majorCities: ['Lagos', 'Kano', 'Ibadan', 'Abuja'] },
  { code: 'KE', name: 'Kenya', dialCode: '+254', majorCities: ['Nairobi', 'Mombasa'] },
  { code: 'MA', name: 'Morocco', dialCode: '+212', majorCities: ['Casablanca', 'Rabat', 'Marrakech'] },

  // Caribbean
  { code: 'DO', name: 'Dominican Republic', dialCode: '+1-809', majorCities: ['Santo Domingo', 'Santiago'] },
  { code: 'CU', name: 'Cuba', dialCode: '+53', majorCities: ['Havana', 'Santiago de Cuba'] },
  { code: 'JM', name: 'Jamaica', dialCode: '+1-876', majorCities: ['Kingston', 'Montego Bay'] },
  { code: 'TT', name: 'Trinidad and Tobago', dialCode: '+1-868', majorCities: ['Port of Spain'] },
  { code: 'PR', name: 'Puerto Rico', dialCode: '+1-787', majorCities: ['San Juan', 'Bayamón', 'Ponce'] },

  // Additional Countries (alphabetically)
  { code: 'AF', name: 'Afghanistan', dialCode: '+93' },
  { code: 'AL', name: 'Albania', dialCode: '+355' },
  { code: 'DZ', name: 'Algeria', dialCode: '+213' },
  { code: 'AD', name: 'Andorra', dialCode: '+376' },
  { code: 'AO', name: 'Angola', dialCode: '+244' },
  { code: 'AM', name: 'Armenia', dialCode: '+374' },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '+994' },
  { code: 'BS', name: 'Bahamas', dialCode: '+1-242' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973' },
  { code: 'BB', name: 'Barbados', dialCode: '+1-246' },
  { code: 'BY', name: 'Belarus', dialCode: '+375' },
  { code: 'BJ', name: 'Benin', dialCode: '+229' },
  { code: 'BT', name: 'Bhutan', dialCode: '+975' },
  { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387' },
  { code: 'BW', name: 'Botswana', dialCode: '+267' },
  { code: 'BN', name: 'Brunei', dialCode: '+673' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226' },
  { code: 'BI', name: 'Burundi', dialCode: '+257' },
  { code: 'KH', name: 'Cambodia', dialCode: '+855' },
  { code: 'CM', name: 'Cameroon', dialCode: '+237' },
  { code: 'CV', name: 'Cape Verde', dialCode: '+238' },
  { code: 'CF', name: 'Central African Republic', dialCode: '+236' },
  { code: 'TD', name: 'Chad', dialCode: '+235' },
  { code: 'CG', name: 'Congo', dialCode: '+242' },
  { code: 'CD', name: 'Congo (DRC)', dialCode: '+243' },
  { code: 'HR', name: 'Croatia', dialCode: '+385' },
  { code: 'CY', name: 'Cyprus', dialCode: '+357' },
  { code: 'DJ', name: 'Djibouti', dialCode: '+253' },
  { code: 'DM', name: 'Dominica', dialCode: '+1-767' },
  { code: 'TL', name: 'East Timor', dialCode: '+670' },
  { code: 'ER', name: 'Eritrea', dialCode: '+291' },
  { code: 'EE', name: 'Estonia', dialCode: '+372' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251' },
  { code: 'FJ', name: 'Fiji', dialCode: '+679' },
  { code: 'GA', name: 'Gabon', dialCode: '+241' },
  { code: 'GM', name: 'Gambia', dialCode: '+220' },
  { code: 'GE', name: 'Georgia', dialCode: '+995' },
  { code: 'GH', name: 'Ghana', dialCode: '+233' },
  { code: 'GD', name: 'Grenada', dialCode: '+1-473' },
  { code: 'GN', name: 'Guinea', dialCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245' },
  { code: 'HT', name: 'Haiti', dialCode: '+509' },
  { code: 'IQ', name: 'Iraq', dialCode: '+964' },
  { code: 'JO', name: 'Jordan', dialCode: '+962' },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965' },
  { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996' },
  { code: 'LA', name: 'Laos', dialCode: '+856' },
  { code: 'LV', name: 'Latvia', dialCode: '+371' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961' },
  { code: 'LS', name: 'Lesotho', dialCode: '+266' },
  { code: 'LR', name: 'Liberia', dialCode: '+231' },
  { code: 'LY', name: 'Libya', dialCode: '+218' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423' },
  { code: 'LT', name: 'Lithuania', dialCode: '+370' },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352' },
  { code: 'MK', name: 'North Macedonia', dialCode: '+389' },
  { code: 'MG', name: 'Madagascar', dialCode: '+261' },
  { code: 'MW', name: 'Malawi', dialCode: '+265' },
  { code: 'MV', name: 'Maldives', dialCode: '+960' },
  { code: 'ML', name: 'Mali', dialCode: '+223' },
  { code: 'MT', name: 'Malta', dialCode: '+356' },
  { code: 'MR', name: 'Mauritania', dialCode: '+222' },
  { code: 'MU', name: 'Mauritius', dialCode: '+230' },
  { code: 'MD', name: 'Moldova', dialCode: '+373' },
  { code: 'MC', name: 'Monaco', dialCode: '+377' },
  { code: 'MN', name: 'Mongolia', dialCode: '+976' },
  { code: 'ME', name: 'Montenegro', dialCode: '+382' },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258' },
  { code: 'MM', name: 'Myanmar', dialCode: '+95' },
  { code: 'NA', name: 'Namibia', dialCode: '+264' },
  { code: 'NP', name: 'Nepal', dialCode: '+977' },
  { code: 'NE', name: 'Niger', dialCode: '+227' },
  { code: 'OM', name: 'Oman', dialCode: '+968' },
  { code: 'QA', name: 'Qatar', dialCode: '+974' },
  { code: 'RW', name: 'Rwanda', dialCode: '+250' },
  { code: 'LC', name: 'Saint Lucia', dialCode: '+1-758' },
  { code: 'WS', name: 'Samoa', dialCode: '+685' },
  { code: 'SM', name: 'San Marino', dialCode: '+378' },
  { code: 'ST', name: 'São Tomé and Príncipe', dialCode: '+239' },
  { code: 'SN', name: 'Senegal', dialCode: '+221' },
  { code: 'RS', name: 'Serbia', dialCode: '+381' },
  { code: 'SC', name: 'Seychelles', dialCode: '+248' },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', dialCode: '+386' },
  { code: 'SB', name: 'Solomon Islands', dialCode: '+677' },
  { code: 'SO', name: 'Somalia', dialCode: '+252' },
  { code: 'SS', name: 'South Sudan', dialCode: '+211' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94' },
  { code: 'SD', name: 'Sudan', dialCode: '+249' },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268' },
  { code: 'SY', name: 'Syria', dialCode: '+963' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886' },
  { code: 'TJ', name: 'Tajikistan', dialCode: '+992' },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255' },
  { code: 'TG', name: 'Togo', dialCode: '+228' },
  { code: 'TO', name: 'Tonga', dialCode: '+676' },
  { code: 'TN', name: 'Tunisia', dialCode: '+216' },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993' },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688' },
  { code: 'UG', name: 'Uganda', dialCode: '+256' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380' },
  { code: 'UZ', name: 'Uzbekistan', dialCode: '+998' },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678' },
  { code: 'VA', name: 'Vatican City', dialCode: '+379' },
  { code: 'YE', name: 'Yemen', dialCode: '+967' },
  { code: 'ZM', name: 'Zambia', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263' },
  { code: 'IR', name: 'Iran', dialCode: '+98', majorCities: ['Tehran', 'Mashhad', 'Isfahan'] },
  { code: 'KP', name: 'North Korea', dialCode: '+850', majorCities: ['Pyongyang'] },
  { code: 'FK', name: 'Falkland Islands', dialCode: '+500' },
  { code: 'GQ', name: 'Equatorial Guinea', dialCode: '+240', majorCities: ['Malabo'] },
  { code: 'EH', name: 'Western Sahara', dialCode: '+212' },
  { code: 'TF', name: 'French Southern Territories', dialCode: '+262' },
  { code: 'SS', name: 'South Sudan', dialCode: '+211' },
  { code: 'CI', name: 'Ivory Coast', dialCode: '+225', majorCities: ['Abidjan', 'Yamoussoukro'] },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675', majorCities: ['Port Moresby'] },
  { code: '_2', name: 'Somaliland', dialCode: '+252' },
  { code: '_1', name: 'Kosovo', dialCode: '+383' },
  { code: 'NC', name: 'New Caledonia', dialCode: '+687', majorCities: ['Nouméa'] },
];

/**
 * Get states/provinces for a given country code
 */
export function getStatesForCountry(countryCode: string): State[] {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return country?.states || [];
}

/**
 * Get cities for a given country and optionally state
 */
export function getCitiesForLocation(countryCode: string, stateCode?: string): string[] {
  const country = COUNTRIES.find(c => c.code === countryCode);

  if (!country) return [];

  // If state is provided and country has states, get cities from that state
  if (stateCode && country.states) {
    const state = country.states.find(s => s.code === stateCode);
    if (state?.cities) return state.cities;
  }

  // Otherwise return major cities of the country
  return country.majorCities || [];
}

/**
 * Get country by code
 */
export function getCountryByCode(countryCode: string): Country | undefined {
  return COUNTRIES.find(c => c.code === countryCode);
}

/**
 * Check if country has states/provinces
 */
export function hasStates(countryCode: string): boolean {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return !!(country?.states && country.states.length > 0);
}

/**
 * Search countries by name
 */
export function searchCountries(query: string): Country[] {
  if (!query) return COUNTRIES;

  const lowerQuery = query.toLowerCase();
  return COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(lowerQuery) ||
    c.code.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Search states by name for a specific country
 */
export function searchStates(countryCode: string, query: string): State[] {
  const states = getStatesForCountry(countryCode);
  if (!query) return states;

  const lowerQuery = query.toLowerCase();
  return states.filter(s =>
    s.name.toLowerCase().includes(lowerQuery) ||
    s.code.toLowerCase().includes(lowerQuery)
  );
}
